import { forwardRef, Ref } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { GetProps, isWeb, styled, TamaguiElement } from '@tamagui/core';

import { ComponentWithViewTracking } from '../../helpers/analytics';
import { AnchorOrButtonProps, useAnchorOrButtonPress } from '../shared/anchorOrButton';
import { StyleableText } from '../Text/Text';

const { link: linkTokens } = tokens;

export type LinkVariant = Exclude<keyof (typeof linkTokens)['color']['content'], 'state'>;
export type LinkSize = keyof (typeof linkTokens)['typography'];

const StyledLink = styled(StyleableText, {
  hoverStyle: {
    cursor: 'pointer',
  },

  focusVisibleStyle: {
    outlineStyle: 'solid',
    outlineWidth: linkTokens.borderWidth.state.default,
    outlineOffset: linkTokens.padding.state.focused.default,
    borderRadius: linkTokens.cornerRadius.state.focused,
  },

  variants: {
    variant: (variant: LinkVariant, { props }) => {
      return {
        color: props.disabled
          ? linkTokens.color.content.state.inactive[variant]
          : linkTokens.color.content[variant],
        textDecorationLine: variant === 'primary' ? 'none' : 'underline',
        hoverStyle: props.disabled
          ? undefined
          : {
              color: linkTokens.color.content.state.hover[variant],
            },
        focusVisibleStyle: {
          outlineColor:
            variant === 'secondaryInverse'
              ? linkTokens.color.border.state.inverse
              : linkTokens.color.border.state.default,
        },
      };
    },

    disabled: {
      true: {
        pointerEvents: 'none',
      },
    },
  } as const,

  defaultVariants: {
    variant: 'primary',
  },
});

export type LinkProps = Omit<GetProps<typeof StyledLink>, 'href' | 'target'> &
  AnchorOrButtonProps & {
    /**
     * The size of the link. Maps to "body" typography tokens. Defaults to
     * "medium".
     */
    size?: LinkSize;
  };

/**
 * The Link component renders inline text that the user can press to navigate to
 * a different page or different section of the current page. Only use this
 * component for navigation, not for actions like toggling a modal or submitting
 * a form (for actions, you should use a button component instead).
 *
 * The `variant` prop determines the color and style of the link. The `size`
 * prop determines the typographic styles of the link (you can also pass
 * `typography` directly if you know what typography token to use).
 *
 * If you don't want the Link component to handle navigation (e.g., because
 * you're using your own Link component from a routing library like React
 * Router), omit the `href` prop. This will make the Link component default to
 * rendering a `<span>` instead of an `<a>` tag, which you can then use as a
 * child of the routing library's Link.
 *
 * On native, if you pass an `href`, the Link component will default to opening
 * the URL in the device's default browser by calling `Linking.openURL`. You can
 * override this behavior by passing an `openURL` prop.
 *
 * The component tracks click events ("linkClickEvent") when you pass a
 * non-empty `analyticsName`. On web, you can also track view events
 * ("linkViewEvent") by passing `trackImpression` as well. Tracking view events
 * is not supported on native for this component — instead, track view events
 * on a parent component.
 */
export const Link = forwardRef(function Link(
  {
    size = 'medium',
    children,
    href,
    onPress,
    openURL,
    analyticsName,
    trackImpression,
    ...props
  }: LinkProps,
  ref: Ref<TamaguiElement>,
) {
  const handlePress = useAnchorOrButtonPress({ href, onPress, openURL, analyticsName });

  const Component = (
    <StyledLink
      href={href}
      tag={href ? 'a' : 'span'}
      typography={linkTokens.typography[size]}
      {...props}
      onPress={handlePress}
    >
      {children}
    </StyledLink>
  );

  /**
   * Only tracking impressions on web because on native we would need to render
   * a View outside the text to measure dimensions (Text doesn't have a
   * `measure` method) but doing that breaks layout of text nested inside other
   * text.
   */
  if (isWeb && analyticsName && trackImpression) {
    return (
      <ComponentWithViewTracking
        Component={Component}
        analyticsName={analyticsName}
        eventName="linkViewEvent"
        ref={ref}
      />
    );
  }

  return Component;
});
